/**
 * Leg status helpers for admin leg create/edit saves.
 */

export const normalizeLegStatusValue = (value) =>
  String(value || "")
    .toLowerCase()
    .trim()
    .replace(/[_\s-]/g, "");

export const isDeliveredLikeLegStatus = (value) => {
  const status = normalizeLegStatusValue(value);
  return status === "delivered" || status === "completed" || status === "paid";
};

const isAssignedLikeLegStatus = (value) => {
  const status = normalizeLegStatusValue(value);
  return status === "assigned" || status === "dispatched";
};

const isPreStartLegStatus = (value) => {
  const status = normalizeLegStatusValue(value);
  return (
    !status ||
    status === "new" ||
    status === "pending" ||
    status === "scheduled" ||
    isAssignedLikeLegStatus(value)
  );
};

const toTimestamp = (value) => {
  if (!value) return null;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? null : parsed;
};

/**
 * Resolve driver id from populated driver object or raw id.
 *
 * @param {object|string|null|undefined} driver
 * @returns {string|null}
 */
export const resolveLegDriverId = (driver) => {
  if (!driver) return null;
  if (typeof driver === "string") return driver;
  return driver._id || driver.id || null;
};

/**
 * Derive next leg status for an admin leg edit save.
 * Preserves status when driver/appointment are unchanged (e.g. equipment-only edits).
 * Never regresses assigned/dispatched legs that still have a driver.
 *
 * @param {object} params
 * @param {string} params.currentStatus
 * @param {string|null} params.nextDriverId
 * @param {Date|string|null} params.nextAppointmentDate
 * @param {string|null} [params.originalDriverId]
 * @param {Date|string|null} [params.originalAppointmentDate]
 * @returns {string|undefined} Next status, or undefined to preserve current
 */
export function resolveLegEditSaveStatus({
  currentStatus,
  nextDriverId,
  nextAppointmentDate,
  originalDriverId = null,
  originalAppointmentDate = null,
}) {
  if (isDeliveredLikeLegStatus(currentStatus)) {
    return undefined;
  }

  const normalizedCurrentStatus = normalizeLegStatusValue(currentStatus);
  const driverChanged =
    String(originalDriverId || "") !== String(nextDriverId || "");
  const appointmentChanged =
    toTimestamp(originalAppointmentDate) !== toTimestamp(nextAppointmentDate);

  if (!driverChanged && !appointmentChanged) {
    return undefined;
  }

  if (!isPreStartLegStatus(currentStatus)) {
    return undefined;
  }

  const isAssignedLike = isAssignedLikeLegStatus(currentStatus);
  const isScheduledLike = normalizedCurrentStatus === "scheduled";

  if (
    !nextDriverId &&
    (isAssignedLike || isScheduledLike) &&
    driverChanged
  ) {
    return "pending";
  }

  if (nextAppointmentDate) {
    if (isAssignedLike && nextDriverId) {
      return undefined;
    }
    if (appointmentChanged || normalizedCurrentStatus === "pending") {
      return "scheduled";
    }
    return undefined;
  }

  if (appointmentChanged && !nextAppointmentDate) {
    if (nextDriverId) return "assigned";
    return "pending";
  }

  if (nextDriverId && normalizedCurrentStatus === "pending" && driverChanged) {
    return nextAppointmentDate ? "scheduled" : "assigned";
  }

  return undefined;
}

/**
 * @param {object} leg
 * @returns {{ driverId: string|null, appointmentDate: *, status: * }}
 */
export function buildLegEditSnapshot(leg = {}) {
  return {
    driverId: resolveLegDriverId(leg.driver),
    appointmentDate: leg.appointmentDate || null,
    status: leg.status,
  };
}
